import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { AuthModal } from "@/components/AuthModal";
import { VideoModal } from "@/components/VideoModal";
import { VideoRow } from "@/components/VideoRow";
import { Hero, MarqueeStrip, Manifesto, Pricing, Footer, ComingSoon } from "@/components/sections";
import { ChatWidget } from "@/components/ChatWidget";
import { LiveCable } from "@/components/LiveCable";
import { NotifyForm } from "@/components/NotifyForm";

export default function Home() {
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [authOpen, setAuthOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const [liveEnabled, setLiveEnabled] = useState(false);
  const previewsRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/videos");
      setVideos(data);
    } catch {}
  };

  useEffect(() => {
    load();
  }, [user]);

  useEffect(() => {
    api.get("/live").then(({ data }) => setLiveEnabled((data.channels || []).length > 0)).catch(() => {});
  }, []);

  const startCheckout = async (plan) => {
    if (!user) {
      setAuthOpen(true);
      toast.info("Sign in to subscribe");
      return;
    }
    try {
      const { data } = await api.post("/payments/checkout", {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Try again.");
    }
  };

  const goPremium = () => {
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
  };

  const startPPV = async (video) => {
    if (!user) {
      setAuthOpen(true);
      toast.info("Sign in to rent this");
      return;
    }
    try {
      const { data } = await api.post("/ppv/checkout", {
        video_id: video.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
    }
  };

  const openVideo = (v) => setActiveVideo(v);

  const featured = videos.filter((v) => v.featured);
  const comingSoon = videos.find((v) => v.category === "Coming Soon");
  const castingRoom = videos.filter((v) => v.category === "The Casting Room");
  const getFit = videos.filter((v) => v.category === "Get Fit With Apple Watts");
  const ppv = videos.filter((v) => v.ppv);
  const variableX = videos.filter(
    (v) => v.category === "The Variable X" || v.title.startsWith("The Variable X")
  );
  const podcast = videos.filter((v) => v.category === "Podcast");
  const SERIES = [
    "Coming Soon",
    "The Casting Room",
    "Get Fit With Apple Watts",
    "The Variable X",
    "Podcast",
    "Pay-Per-View",
  ];
  const previews = videos.filter(
    (v) =>
      !v.ppv &&
      (v.category === "Free Previews" ||
        (!v.is_premium && !v.featured && !SERIES.includes(v.category)))
  );
  const originals = videos.filter(
    (v) => v.is_premium && !v.ppv && !SERIES.includes(v.category)
  );

  return (
    <div className="bg-[#050505] min-h-screen">
      <Navbar onAuth={() => setAuthOpen(true)} onSubscribe={goPremium} />

      <Hero
        onExplore={() => previewsRef.current?.scrollIntoView({ behavior: "smooth" })}
        onSubscribe={goPremium}
      />

      <MarqueeStrip />

      <div ref={previewsRef} id="previews" />
      {featured.length > 0 && (
        <VideoRow title="Featured" subtitle="On The Network" videos={featured} onOpen={openVideo} />
      )}
      <VideoRow title="Free Previews" subtitle="Watch Now — No Login" videos={previews} onOpen={openVideo} />

      <VideoRow title="The Casting Room" subtitle="Original Series · Casting For The Network" videos={castingRoom} onOpen={openVideo} />

      <VideoRow title="Get Fit With Apple Watts" subtitle="Original Series · Fitness" videos={getFit} onOpen={openVideo} />

      <Manifesto />

      <ComingSoon show={comingSoon} onOpen={openVideo} />

      <VideoRow title="The Variable X" subtitle="Original Series · Season One" videos={variableX} onOpen={openVideo} fullColor />

      <VideoRow title="Podcast" subtitle="Original Series · Coming Soon" videos={podcast} onOpen={openVideo} fullColor />

      {podcast.length > 0 && (
        <section className="px-6 md:px-16 pb-12 -mt-2" data-testid="podcast-notify">
          <p className="font-mono-imn text-[11px] tracking-widest text-gold uppercase mb-3">
            Be first to hear the Podcast
          </p>
          <NotifyForm show="It's My Network Podcast" testid="notify-podcast" />
        </section>
      )}

      <div id="originals" />
      <VideoRow title="Premium Originals" subtitle="Subscribers Only" videos={originals} onOpen={openVideo} />

      <VideoRow title="Pay-Per-View" subtitle="Rent · One-Time · Live Events" videos={ppv} onOpen={openVideo} fullColor />

      <LiveCable onGetPass={() => startCheckout("live")} />

      <Pricing onSelect={startCheckout} showLivePass={liveEnabled} />

      <Footer />

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <ChatWidget />
      <VideoModal
        video={activeVideo}
        onClose={() => setActiveVideo(null)}
        onSubscribe={() => {
          setActiveVideo(null);
          goPremium();
        }}
        onPPV={(v) => {
          setActiveVideo(null);
          startPPV(v);
        }}
      />
    </div>
  );
}
